import { useGoogleMap } from "@react-google-maps/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/* eslint-disable no-undef */

export const DRAWING_MODES = {
  MARKER: "marker",
  POLYGON: "polygon",
};

const CONTROL_STYLE = {
  background: "#fff",
  border: "2px solid #fff",
  borderRadius: "3px",
  boxShadow: "0 2px 6px rgba(0,0,0,.3)",
  cursor: "pointer",
  margin: "8px 8px 0 8px",
  padding: "6px 10px",
  fontSize: "13px",
  lineHeight: "18px",
};

const ACTIVE_CONTROL_STYLE = {
  ...CONTROL_STYLE,
  background: "#e8f0fe",
  border: "2px solid #4285f4",
};

const CustomDrawingManager = ({
  drawingMode = null,
  drawingControl = true,
  drawingControlOptions,
  markerOptions,
  polygonOptions,
  options = {},

  onLoad,
  onUnmount,
  onOverlayComplete,
  onMarkerComplete,
  onPolygonComplete,
  onDrawingModeChange,
}) => {
  const map = useGoogleMap();

  const listenersRef = useRef([]);
  const overlayListenersRef = useRef([]);
  const controlRef = useRef(null);
  const controlIndexRef = useRef(null);
  const controlPositionRef = useRef(null);
  const apiRef = useRef(null);
  const polygonPathRef = useRef([]);
  const previewPolygonRef = useRef(null);
  const polygonsRef = useRef([]);

  const [activeDrawingMode, setActiveDrawingMode] = useState(drawingMode);

  const drawingModes = useMemo(() => {
    const configuredModes = drawingControlOptions?.drawingModes;

    if (Array.isArray(configuredModes) && configuredModes.length) {
      return configuredModes;
    }

    return [];
  }, [drawingControlOptions]);

  const clearListeners = useCallback(() => {
    listenersRef.current.forEach(listener => listener.remove());
    listenersRef.current = [];
  }, []);

  const clearOverlayListeners = useCallback(() => {
    overlayListenersRef.current.forEach(listener => listener.remove());
    overlayListenersRef.current = [];
  }, []);

  const clearPreviewPolygon = useCallback(() => {
    if (previewPolygonRef.current) {
      previewPolygonRef.current.setMap(null);
      previewPolygonRef.current = null;
    }

    polygonPathRef.current = [];
  }, []);

  const removeControl = useCallback(() => {
    if (!map || controlIndexRef.current === null || controlPositionRef.current === null) {
      if (controlRef.current) {
        controlRef.current.remove();
        controlRef.current = null;
      }

      controlIndexRef.current = null;
      controlPositionRef.current = null;
      return;
    }

    const controls = map.controls[controlPositionRef.current];

    if (controls?.getAt(controlIndexRef.current) === controlRef.current) {
      controls.removeAt(controlIndexRef.current);
    } else if (controls) {
      for (let index = controls.getLength() - 1; index >= 0; index -= 1) {
        if (controls.getAt(index) === controlRef.current) {
          controls.removeAt(index);
          break;
        }
      }
    }

    controlRef.current = null;
    controlIndexRef.current = null;
    controlPositionRef.current = null;
  }, [map]);

  const stopDrawing = useCallback(() => {
    setActiveDrawingMode(null);
    clearPreviewPolygon();
  }, [clearPreviewPolygon]);

  const emitOverlayComplete = useCallback(
    event => {
      if (onOverlayComplete) {
        onOverlayComplete(event);
      }
    },
    [onOverlayComplete]
  );

  const createMarker = useCallback(
    latLng => {
      const position = {
        lat: typeof latLng.lat === "function" ? latLng.lat() : latLng.lat,
        lng: typeof latLng.lng === "function" ? latLng.lng() : latLng.lng,
      };

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position,
        ...(markerOptions || {}),
      });

      if (onMarkerComplete) {
        onMarkerComplete(marker);
      }

      emitOverlayComplete({
        type: DRAWING_MODES.MARKER,
        overlay: marker,
      });

      stopDrawing();
    },
    [map, markerOptions, onMarkerComplete, emitOverlayComplete, stopDrawing]
  );

  const updatePreviewPolygon = useCallback(
    path => {
      if (!previewPolygonRef.current) {
        previewPolygonRef.current = new google.maps.Polygon({
          map,
          paths: path,
          editable: false,
          clickable: false,
          strokeWeight: 2,
          fillOpacity: 0.2,
          ...(polygonOptions || {}),
        });

        return;
      }

      previewPolygonRef.current.setPath(path);
    },
    [map, polygonOptions]
  );

  const finishPolygon = useCallback(() => {
    if (polygonPathRef.current.length < 3) {
      clearPreviewPolygon();
      return;
    }

    const polygon = new google.maps.Polygon({
      map,
      paths: polygonPathRef.current,
      clickable: activeDrawingMode !== DRAWING_MODES.MARKER,
      ...(polygonOptions || {}),
    });

    polygonsRef.current = [...polygonsRef.current, polygon];

    if (onPolygonComplete) {
      onPolygonComplete(polygon);
    }

    emitOverlayComplete({
      type: DRAWING_MODES.POLYGON,
      overlay: polygon,
    });

    stopDrawing();
  }, [
    map,
    polygonOptions,
    activeDrawingMode,
    onPolygonComplete,
    emitOverlayComplete,
    stopDrawing,
    clearPreviewPolygon,
  ]);

  const handleMapClick = useCallback(
    event => {
      if (!activeDrawingMode || !event.latLng) return;

      if (activeDrawingMode === DRAWING_MODES.MARKER) {
        createMarker(event.latLng);
        return;
      }

      if (activeDrawingMode === DRAWING_MODES.POLYGON) {
        polygonPathRef.current = [...polygonPathRef.current, event.latLng];
        updatePreviewPolygon(polygonPathRef.current);
      }
    },
    [activeDrawingMode, createMarker, updatePreviewPolygon]
  );

  const handleKeyDown = useCallback(
    event => {
      if (activeDrawingMode !== DRAWING_MODES.POLYGON) return;

      if (event.key === "Enter") {
        event.preventDefault();
        finishPolygon();
      }
    },
    [activeDrawingMode, finishPolygon]
  );

  const createControlButton = useCallback(
    ({ label, mode }) => {
      const button = document.createElement("button");
      button.type = "button";
      button.innerText = label;

      Object.assign(button.style, activeDrawingMode === mode ? ACTIVE_CONTROL_STYLE : CONTROL_STYLE);

      button.onclick = () => {
        clearPreviewPolygon();
        setActiveDrawingMode(prevMode => (prevMode === mode ? null : mode));
      };

      return button;
    },
    [activeDrawingMode, clearPreviewPolygon]
  );

  const renderControl = useCallback(() => {
    if (!map || !drawingControl) {
      removeControl();
      return;
    }

    removeControl();

    const control = document.createElement("div");
    control.style.display = "flex";

    if (drawingModes.includes(DRAWING_MODES.MARKER)) {
      control.appendChild(createControlButton({ label: "Marker", mode: DRAWING_MODES.MARKER }));
    }

    if (drawingModes.includes(DRAWING_MODES.POLYGON)) {
      control.appendChild(createControlButton({ label: "Polygon", mode: DRAWING_MODES.POLYGON }));
    }

    const position = drawingControlOptions?.position || google.maps.ControlPosition.TOP_RIGHT;
    const controlIndex = map.controls[position].push(control) - 1;

    controlRef.current = control;
    controlIndexRef.current = controlIndex;
    controlPositionRef.current = position;
  }, [map, drawingControl, drawingModes, drawingControlOptions, createControlButton, removeControl]);

  useEffect(() => {
    if (!map || !google?.maps?.event) return;

    apiRef.current = {
      setMap: nextMap => {
        if (!nextMap) {
          clearListeners();
          clearOverlayListeners();
          clearPreviewPolygon();
          removeControl();
        }
      },
      setOptions: nextOptions => {
        if (nextOptions?.drawingMode !== undefined) {
          setActiveDrawingMode(nextOptions.drawingMode);
        }
      },
    };

    if (onLoad) {
      onLoad(apiRef.current);
    }

    return () => {
      clearListeners();
      clearOverlayListeners();
      clearPreviewPolygon();
      removeControl();

      if (onUnmount) {
        onUnmount(apiRef.current);
      }

      apiRef.current = null;
    };
  }, [map, onLoad, onUnmount, clearListeners, clearOverlayListeners, clearPreviewPolygon, removeControl]);

  useEffect(() => {
    if (!map || !google?.maps?.event) return;

    clearListeners();

    listenersRef.current.push(map.addListener("click", handleMapClick));

    return clearListeners;
  }, [map, handleMapClick, clearListeners]);

  useEffect(() => {
    if (!google?.maps?.event) return;

    clearOverlayListeners();

    polygonsRef.current.forEach(polygon => {
      polygon.setOptions({
        clickable: activeDrawingMode !== DRAWING_MODES.MARKER,
      });

      if (activeDrawingMode === DRAWING_MODES.MARKER) {
        overlayListenersRef.current.push(
          polygon.addListener("click", event => {
            if (event.latLng) {
              createMarker(event.latLng);
            }
          })
        );
      }
    });

    return clearOverlayListeners;
  }, [activeDrawingMode, createMarker, clearOverlayListeners]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!map) return;

    const previousDraggableCursor = map.get("draggableCursor");
    const previousDoubleClickZoom = options.disableDoubleClickZoom;

    map.setOptions({
      draggableCursor: activeDrawingMode ? "crosshair" : previousDraggableCursor,
      disableDoubleClickZoom: previousDoubleClickZoom,
    });

    return () => {
      map.setOptions({
        draggableCursor: previousDraggableCursor,
        disableDoubleClickZoom: previousDoubleClickZoom,
      });
    };
  }, [map, activeDrawingMode, options.disableDoubleClickZoom]);

  useEffect(() => {
    renderControl();

    return removeControl;
  }, [renderControl, removeControl]);

  useEffect(() => {
    setActiveDrawingMode(drawingMode);
  }, [drawingMode]);

  useEffect(() => {
    if (onDrawingModeChange) {
      onDrawingModeChange(activeDrawingMode);
    }
  }, [activeDrawingMode, onDrawingModeChange]);

  return null;
};

export default CustomDrawingManager;
